import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';

const InteractiveLinesSif: React.FC = () => {
  const ring1Ref = useRef<SVGGElement>(null);
  const ring2Ref = useRef<SVGGElement>(null);
  const ring3Ref = useRef<SVGGElement>(null);

  // Slow rotation animation via requestAnimationFrame
  useEffect(() => {
    let angle1 = 0;
    let angle2 = 0;
    let angle3 = 0;
    let raf: number;

    const tick = () => {
      angle1 += 0.04;
      angle2 -= 0.025;
      angle3 += 0.015;
      if (ring1Ref.current) ring1Ref.current.setAttribute('transform', `rotate(${angle1}, 500, 300)`);
      if (ring2Ref.current) ring2Ref.current.setAttribute('transform', `rotate(${angle2}, 500, 300)`);
      if (ring3Ref.current) ring3Ref.current.setAttribute('transform', `rotate(${angle3}, 500, 300)`);
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const fadePath = {
    hidden: { pathLength: 0, opacity: 0 },
    visible: (d: number) => ({
      pathLength: 1,
      opacity: 1,
      transition: { duration: 2, delay: d, ease: [0.22, 1, 0.36, 1] },
    }),
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const dots = Array.from({ length: 10 }, () => ({
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 50 + 10,
    delay: Math.random() * 1.5 + 0.5,
  }));

  return (
    <div className="absolute inset-0 overflow-hidden">
      <motion.div
        className="absolute inset-0"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <svg
          className="absolute inset-0 w-full h-full"
          viewBox="0 0 1000 600"
          preserveAspectRatio="xMidYMid slice"
        >
          <defs>
            <filter id="sif-glow">
              <feGaussianBlur stdDeviation="2.5" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>
          </defs>

          {/* Static outer thin circle */}
          <motion.circle
            cx="500" cy="300" r="280"
            stroke="#8B96D4" strokeWidth="0.8" fill="none"
            strokeDasharray="4,8"
            variants={fadePath} initial="hidden" animate="visible" custom={0.3}
          />

          {/* Rotating group 1 — dashed medium ring + 4 radial spokes */}
          <g ref={ring1Ref}>
            <circle cx="500" cy="300" r="200"
              stroke="#8B96D4" strokeWidth="1.2" fill="none"
              strokeDasharray="12,8" opacity={0.8}
            />
            {[0, 90, 180, 270].map((deg, i) => {
              const rad = (deg * Math.PI) / 180;
              const x2 = 500 + Math.cos(rad) * 200;
              const y2 = 300 + Math.sin(rad) * 200;
              return (
                <line key={i} x1="500" y1="300" x2={x2} y2={y2}
                  stroke="#8B96D4" strokeWidth="0.9" opacity={0.55}
                  strokeDasharray="4,6"
                />
              );
            })}
          </g>

          {/* Rotating group 2 — solid small ring, counter-clockwise */}
          <g ref={ring2Ref}>
            <circle cx="500" cy="300" r="120"
              stroke="#8B96D4" strokeWidth="1.4" fill="none"
              strokeDasharray="20,6" opacity={0.9}
              filter="url(#sif-glow)"
            />
            {[45, 135, 225, 315].map((deg, i) => {
              const rad = (deg * Math.PI) / 180;
              const x2 = 500 + Math.cos(rad) * 120;
              const y2 = 300 + Math.sin(rad) * 120;
              return (
                <line key={i} x1="500" y1="300" x2={x2} y2={y2}
                  stroke="#8B96D4" strokeWidth="0.8" opacity={0.65}
                />
              );
            })}
          </g>

          {/* Rotating group 3 — very slow large dashed ring */}
          <g ref={ring3Ref}>
            <circle cx="500" cy="300" r="340"
              stroke="#8B96D4" strokeWidth="0.7" fill="none"
              strokeDasharray="3,12" opacity={0.55}
            />
          </g>

          {/* Center dot */}
          <motion.circle
            cx="500" cy="300" r="4"
            fill="#8B96D4" opacity={1}
            filter="url(#sif-glow)"
            variants={fadePath} initial="hidden" animate="visible" custom={1.2}
          />

          {/* Thin static horizontal lines */}
          {[0.2, 0.5, 0.8].map((pos, i) => (
            <motion.line
              key={`hl-${i}`}
              x1="0" y1={600 * pos} x2="1000" y2={600 * pos}
              stroke="#8B96D4" strokeWidth="0.6" opacity={0.45}
              variants={fadePath} initial="hidden" animate="visible" custom={0.4 + i * 0.15}
            />
          ))}
        </svg>

      </motion.div>
    </div>
  );
};

export default InteractiveLinesSif;
